import { Action, initialState, State } from 'store/store';
import {
  getAllTopicsOrdered,
  getInteractionsFromTopicAndState,
} from 'selectors/block';
import { Block } from 'factories/block';
import isAndroidSamsung from 'services/isAndroidSamsung';
import isAppleComputerInc from 'services/isAppleComputerInc';
import { removeUndefinedEntriesFromObject } from 'services/removeUndefinedEntriesFromObject';
import {
  oldStylesToNewMap,
  oldDataToNewMap,
} from 'factories/interactions/continueBtn';

import { preprocessTypeSettings } from 'services/preprocessTypeSettings';

const isDevice = isAppleComputerInc() || isAndroidSamsung();

export const preprocessModule = (
  state: State = initialState,
  { data }: Action
): State => {
  const topics = getAllTopicsOrdered(state.blocks);
  const nextInteractionMap = {};
  const idToSIdMap = {};
  const sIdToIdMap = {};

  let interactionsOpen: string[] = [];
  let interactionsLocked: string[] = [];
  // *
  // when we're wanting to test this locally we need to include this line
  // since there's different timings to when savedState loads from scorm/localStorage
  // state.interactionsLocked || [];

  // it's likely ( for sure ) that moduleIsFreeScrolling will not exist on existing modules
  // that already have their state defined on the server
  const moduleIsFreeScrolling = state.theme?.moduleIsFreeScrolling || {
    desktop: false,
    device: false,
  };

  const {
    desktop: moduleIsFreeScrollingDesktop,
    device: moduleIsFreeScrollingDevice,
  } = moduleIsFreeScrolling;

  let topicsIdSIdMapping = {
    idToSId: {},
    sIdToId: {},
  };

  topics.forEach((topic: Block) => {
    const sId = topic.props?.sId;
    if (sId) {
      topicsIdSIdMapping.idToSId[topic.id] = sId;
      topicsIdSIdMapping.sIdToId[sId] = topic.id;
    }

    const interactions = getInteractionsFromTopicAndState(
      state as any,
      (Object.values(state.blocks) as any).find(($: any) => $.id === topic.id)
    );

    interactions.forEach((interaction, index) => {
      const { id } = interaction;
      const { sId, lockDown } = (interaction as any)?.props;

      if (!isDevice) {
        moduleIsFreeScrollingDesktop && interactionsOpen.push(id);
        !moduleIsFreeScrollingDesktop &&
          lockDown &&
          interactionsLocked.push(id);
      }

      if (isDevice) {
        moduleIsFreeScrollingDevice && interactionsOpen.push(id);
        !moduleIsFreeScrollingDevice && lockDown && interactionsLocked.push(id);
      }

      idToSIdMap[id] = sId;
      sIdToIdMap[sId] = id;

      if (index != interactions.length - 1) {
        nextInteractionMap[id] = interactions[index + 1].id;
      }

      if (interaction?.meta?.subType === 'TopicPortal') {
        const { selectedTopics } = interaction?.props || {};

        if (
          selectedTopics &&
          Array.isArray(selectedTopics) &&
          selectedTopics.some(entry => entry.toString().includes('-'))
        ) {
          //
          interaction.props!.selectedTopics = selectedTopics.map(
            entry => state.blocks[entry]?.props?.sId || entry
          );

          // console.log(
          //   'one time updating selected topics references from id to sId ?',
          //   interaction?.props?.selectedTopics
          // );
        }
      }
    });
  });

  let assessmentQuestionIdSIdMapping = {
    idToSId: {},
    sIdToId: {},
  };

  Object.values(state.blocks).forEach((entry, i) => {
    if (entry?.type === 'TopicPortalContainer') {
      //
      const topicPortalMedias = entry?.children as Block[];

      topicPortalMedias?.forEach((tPM, index) => {
        const tPMBlock = state.blocks[tPM?.id];

        if (!tPMBlock) {
          return;
        }

        if (!tPMBlock?.props?.topicSId) {
          //
          if (!tPMBlock?.props) {
            tPMBlock.props = {};
          }

          console.log(
            `TopicPortalMedia is missing topicSId. update : ${topics[index]?.props?.sId}`
          );

          tPMBlock.props.topicSId = topics[index]?.props?.sId;
        }
      });
    }

    const assessmentQuestion =
      entry.type.toLowerCase() === 'assessmentquestion';

    if (assessmentQuestion && entry?.props) {
      const { sId } = entry.props;
      assessmentQuestionIdSIdMapping.idToSId[entry.id] = sId;
      assessmentQuestionIdSIdMapping.sIdToId[sId] = entry.id;
    }

    const subType = entry?.meta?.subType;

    //
    // get the root certificate interaction
    if (subType && subType === 'Certificate') {
      const certificateInteraction = entry;

      // if it has ' imgSrc ' we can know it was added incorrectly
      // from the EditCertificate
      //
      const {
        imgSrc,
        imageId,
        imgWidth,
        imgHeight,
      } = (certificateInteraction as any)?.props;

      let certificate = state.blocks[(entry?.children?.[1] as Block)?.id]
        ?.children?.[0] as Block;

      certificate = certificate && state.blocks[certificate.id];

      // if the interaction has imgSrc. but we don't yet have img values on the certificate ( props ) itself
      // move them over
      if (imgSrc && !certificate?.props?.src) {
        certificate.props = {
          src: imgSrc,
          imageId,
          imgWidth,
          imgHeight,
        };

        delete certificateInteraction.props?.imgSrc;
        delete certificateInteraction.props?.imageId;
        delete certificateInteraction.props?.imgWidth;
        delete certificateInteraction.props?.imgHeight;
      }
    }

    if (entry?.type === 'SelectOptions') {
      (entry?.children as any[])?.forEach((option, index) => {
        const optionBlock = state.blocks[option.id] as Block;

        if (optionBlock && optionBlock?.props) {
          const { correctAnswer } = optionBlock.props;

          if (typeof correctAnswer === 'object') {
            console.log(
              '___ transform select option correctAnswer from object :',
              correctAnswer,
              '>>>'
            );
            optionBlock.props.correctAnswer = Object.values(correctAnswer)[0];
            //
            console.log('to index :', optionBlock.props.correctAnswer, '___');
          }
        }
      });
    }

    if (subType && ['continue', 'editableButton'].includes(subType)) {
      //

      const { props = {} } = entry;
      const { version } = props;

      if (version && version === 1) {
        return;
      }

      const updatedStyleProps = Object.keys(props).reduce((acc, curr) => {
        const newKey = oldStylesToNewMap[curr];

        // move this out later. just want to see it inline for the moment
        // these are the 'local' properties that could be updated by the author in older modules
        //
        const hasLocalMap = {
          // the list of local props that can have been set in the editor

          // localBackgroundColor: true,
          // localColor: true,
          // localButtonHover: color ? true : false,
          // localButtonShadow: true,
          // localButtonOutline: true,
          // localUseLargeButton: true,
          // localShape: true,
          // localBorderRadius: true,

          background: 'localBackgroundColor',
          color: 'localColor',
          buttonHover: 'localButtonHover',
          buttonShadow: 'localButtonShadow',
          buttonOutline: 'localButtonOutline',
          useLargeButton: 'localUseLargeButton',
          buttonShape: 'localShape',
          borderRadius: 'localBorderRadius',
        };

        let hasValue = ![undefined, '', null].includes(props[curr]);

        if (hasLocalMap[curr] && !props[hasLocalMap[curr]]) {
          // if we have this legacy prop
          // and its value HAS NOT been set explicitly. the ' local ' prefix
          // then hasValue should be considered false

          // the intention is to strip out any default props / values that would interfere
          // with our updated theming

          hasValue = false;
        }

        let updateBackgroundOpacityForOutline;

        // this is the dance to save real old buttons from the ' keyline ' style we went with
        // if theme.buttonOutline or prop.buttonOutline the rendered effect sets the backgroundOpacity
        // of the button to 0. but that's never reflected in the settings. only a runtime evaluation and display
        // so we're gonna actually update it on the prop so our settings are in sync
        //
        if (state.theme.buttonOutline || props.buttonOutline) {
          if ([undefined, '', null].includes(props.buttonBackgroundOpacity)) {
            updateBackgroundOpacityForOutline = true;
          }
        }

        const updatedProps = {
          ...acc,
          ...(newKey &&
            hasValue && {
              [newKey]: props[curr],
            }),
          /*
          we're leaving this transform out now since it has caused problems
          for super old old modules where the buttonOutline has been baked into templates
          and spread wide
          
          // ...(updateBackgroundOpacityForOutline && {
          //   backgroundOpacity: 0,
          // }),
          */
        };

        return updatedProps;
      }, {});

      const updatedDataProps = Object.keys(props).reduce((acc, curr) => {
        const newKey = oldDataToNewMap[curr];
        const hasValue = ![undefined, '', null].includes(props[curr]);

        return {
          ...acc,
          ...(newKey &&
            hasValue && {
              [newKey]: props[curr],
            }),
        };
      }, {});

      const $ = {};

      for (const prop in props) {
        if (oldDataToNewMap[prop]) {
          $[prop] = props[prop];
        }
      }

      entry.props = {
        ...props,
        ...updatedStyleProps,

        version: 1,
        // might want to tidy up and move 'legacy' props to
        // LEGACY_PROPS: props,
      };
    }
  });

  let mainButton;

  if (!state.theme?.buttons) {
    //
    console.log(
      `updating buttons and theme ( theme.buttons ) for 'legacy' modules on boot`.toUpperCase()
    );
    mainButton = getExistingButtonStylesFromTheme(state.theme);
  }

  const { theme } = state;
  const typeSettings = preprocessTypeSettings(theme);

  const newState = {
    ...state,
    ...data,
    interactionsOpen,
    interactionsLocked,
    ...{ nextInteractionMap },
    ...{ idToSIdMap },
    ...{ sIdToIdMap },
    topicsIdSIdMapping,
    assessmentQuestionIdSIdMapping,

    theme: {
      ...state.theme,

      ...(typeSettings && {
        typeSettings: {
          ...typeSettings,
        },
      }),
    },

    ...(mainButton && {
      theme: {
        ...state.theme,
        buttons: {
          ...state.theme.buttons,
          main: mainButton,
        },
      },
    }),
  };

  return newState;
};

function getExistingButtonStylesFromTheme(theme) {
  const {
    buttonBackgroundColor,
    buttonBorderRadius,
    buttonOutline,
    buttonShadow,
    buttonShape,
    useLargeButton,
    buttonTextColor,
    buttonHover,
  } = theme;

  return removeUndefinedEntriesFromObject({
    backgroundColor: buttonBackgroundColor,
    borderRadius: buttonBorderRadius,
    outline: buttonOutline,
    shadow: buttonShadow,
    shape: buttonShape,
    useLargeButton,
    textColor: buttonTextColor,
    backgroundColorOnHover: buttonHover,
  });
}
